// components/Reglement/ReglementFilters.tsx

import React, { useEffect, useState } from 'react';
import { GeneriqueDataType, TypeReglement, TypeTiers } from '../../constants/enums';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import EmployeAutoC from '../AutoComplete/EmployeAutoC';
import SelectCaisse from '../Selects/SelectCaisse';
import SelectNatureReglement from '../Selects/SelectNatureReglement';
import SelectModeReglement from '../Selects/SelectModeReglement';
import SelectCategorie from '../Selects/SelectCategorie';
import SelectDepartement from '../Selects/SelectDepartement';
import { Departement, GeneriqueData, Reglement } from '../../models/models';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import AppFilterGrid, { AppFilterResetButton } from '../layout/AppFilterGrid';
import { fetchReglementData, setFilters } from '../../reduxStore/ReglementSlice';
import { labels } from '../../constants/labels';
import { AppDispatch } from '../../reduxStore/Store';
import { useDispatch, useSelector } from 'react-redux';

const FILTERS_RESET = {
  type_reglement: TypeReglement.ENCAISSEMENT as 'ENCAISSEMENT',
  sens: 'C' as 'C',
  tiers_type: null,
  caisse_id: undefined,
  caisse: null,
  tiers_id: null,
  tiers: null,
  nature_id: null,
  nature: null,
  categorie_nature_id: null,
  categorie_nature: null,
  mode_paiement_id: null,
  mode_paiement: null,
  date_start: undefined,
  date_end: undefined,
  montant: null,
  departement_id: null,
  departement: null,
  offset: 0,
};

// Valeur de "mode" UI pour distinguer paiement fournisseur vs employé
// (tous deux type_reglement=PAIEMENT). Ne sert qu'à piloter le sélecteur de tiers.
const PAIEMENT_EMPLOYE = 'PAIEMENT_EMPLOYE';

const ReglementFilters: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters, successMessage } = useSelector((state: any) => state.ReglementSlice);
  const { isAllMode } = useSelector((state: any) => state.AppSlice);
  // Les filtres Redux survivent à la navigation : au montage, le mode s'y recale
  // (« Paiement employé » = PAIEMENT + tiers_type EMPLOYE, sinon retomberait sur fournisseur).
  const [mode, setMode] = useState<string>(() =>
    filters.type_reglement === TypeReglement.PAIEMENT && filters.tiers_type === TypeTiers.EMPLOYE
      ? PAIEMENT_EMPLOYE
      : filters.type_reglement || TypeReglement.ENCAISSEMENT
  );
  // Sélecteur de tiers déduit du mode (une seule source de vérité).
  const tiersType =
    mode === PAIEMENT_EMPLOYE ? TypeTiers.EMPLOYE
    : mode === TypeReglement.PAIEMENT ? TypeTiers.FOURNISSEUR
    : TypeTiers.CLIENT;
  const [montantLocal, setMontantLocal] = useState<string>(filters.montant != null ? String(filters.montant) : '');

  // Debounce montant
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setFilters({
        ...filters,
        montant: montantLocal !== '' ? Number(montantLocal) : null,
        offset: 0,
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [montantLocal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchReglementData(filters));
  }, [dispatch, filters, successMessage]);

  const handleTiersChange = (tiers: any | null) => {
    dispatch(setFilters({ ...filters, tiers_id: tiers ? tiers.id : null, tiers, offset: 0 }));
  };

  const tiersAutoc = (type: TypeTiers) => {
    switch (type) {
      case TypeTiers.CLIENT:
        return <ClientAutoC onClientChange={handleTiersChange} selectedClient={filters.tiers} />;
      case TypeTiers.FOURNISSEUR:
        return <FournisseurAutoC onFournisseurChange={handleTiersChange} selectedFournisseur={filters.tiers} />;
      case TypeTiers.EMPLOYE:
        return <EmployeAutoC onEmployeChange={handleTiersChange} selectedEmploye={filters.tiers} />;
      default:
        return null;
    }
  };

  const handleDepartementChange = (departement: Departement | null) => {
    dispatch(setFilters({ ...filters, departement_id: departement ? departement.id : null, departement, offset: 0 }));
  };

  const handleOnCaisseChange = (caisse: GeneriqueData | null) => {
    dispatch(setFilters({ ...filters, caisse_id: caisse ? caisse.id : undefined, caisse, offset: 0 }));
  };

  const handleOnNatureChange = (nature: GeneriqueData | null) => {
    dispatch(setFilters({ ...filters, nature_id: nature ? nature.id : null, nature, offset: 0 }));
  };

  const handleOnCategorieNatureChange = (categorieId: number | null) => {
    dispatch(setFilters({ ...filters, categorie_nature_id: categorieId, categorie_nature: categorieId, offset: 0 }));
  };

  const handleSensChange = (event: SelectChangeEvent) => {
    dispatch(setFilters({ ...filters, sens: event.target.value as 'C' | 'D' | '', offset: 0 }));
  };

  const handleOnModeChange = (mode: GeneriqueData | null) => {
    dispatch(setFilters({ ...filters, mode_paiement_id: mode ? mode.id : null, mode_paiement: mode, offset: 0 }));
  };

  const handleModeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setMode(value);
    switch (value) {
      case TypeReglement.ENCAISSEMENT:
        dispatch(setFilters({ ...filters, type_reglement: TypeReglement.ENCAISSEMENT, sens: 'C', tiers_type: null, tiers_id: null, tiers: null, nature_id: null, nature: null, categorie_nature_id: null, categorie_nature: null, offset: 0 }));
        break;
      case TypeReglement.PAIEMENT:
        dispatch(setFilters({ ...filters, type_reglement: TypeReglement.PAIEMENT, sens: 'D', tiers_type: TypeTiers.FOURNISSEUR, tiers_id: null, tiers: null, nature_id: null, nature: null, categorie_nature_id: null, categorie_nature: null, offset: 0 }));
        break;
      case PAIEMENT_EMPLOYE:
        dispatch(setFilters({ ...filters, type_reglement: TypeReglement.PAIEMENT, sens: 'D', tiers_type: TypeTiers.EMPLOYE, tiers_id: null, tiers: null, nature_id: null, nature: null, categorie_nature_id: null, categorie_nature: null, offset: 0 }));
        break;
      case TypeReglement.MOUVEMENT:
        dispatch(setFilters({ ...filters, type_reglement: TypeReglement.MOUVEMENT, sens: '', tiers_type: null, tiers_id: null, tiers: null, offset: 0 }));
        break;
    }
  };

  const handleDateChange = (field: 'date_start' | 'date_end') => (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ ...filters, [field]: event.target.value || undefined, offset: 0 }));
  };

  const handleMontantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMontantLocal(event.target.value);
  };

  const handleReset = () => {
    setMontantLocal('');
    setMode(TypeReglement.ENCAISSEMENT);
    dispatch(setFilters({ ...filters, ...FILTERS_RESET }));
  };

  return (
    <AppFilterGrid>
      <FormControl fullWidth size="small">
        <InputLabel id="type_reglement">Type de règlement</InputLabel>
        <Select
          labelId="type_reglement"
          value={mode || ''}
          onChange={handleModeChange}
          label="Type de règlement"
        >
          <MenuItem value={TypeReglement.ENCAISSEMENT}>{labels.reglement} {labels.client}</MenuItem>
          <MenuItem value={TypeReglement.PAIEMENT}>{labels.reglement} {labels.fournisseur}</MenuItem>
          <MenuItem value={PAIEMENT_EMPLOYE}>{labels.reglement} {labels.employe}</MenuItem>
          <MenuItem value={TypeReglement.MOUVEMENT}>Mouvement De Caisse</MenuItem>
        </Select>
      </FormControl>

      <SelectCaisse onCaisseChange={handleOnCaisseChange} selectedCaisse={filters.caisse} includeAllOption />

      {/* departement_id — filtre utile seulement en mode "Tous départements" :
          dans un contexte de département actif, tout est déjà scopé dessus. */}
      {isAllMode && (
        <SelectDepartement
          onDepartementChange={handleDepartementChange}
          selectedDepartement={filters.departement ?? null}
          allowAll
        />
      )}

      <SelectModeReglement
        onModeReglementChange={handleOnModeChange}
        selectedModeReglement={filters.mode_paiement}
        includeAllOption
      />

      {filters.type_reglement !== TypeReglement.MOUVEMENT && tiersAutoc(tiersType)}

      {filters.type_reglement === TypeReglement.MOUVEMENT && (
        <SelectCategorie
          categoryType={GeneriqueDataType.CATEGORIE_NATURE_REGLEMENT}
          label="Catégorie de nature"
          value={filters.categorie_nature_id ?? null}
          onChange={handleOnCategorieNatureChange}
        />
      )}

      {filters.type_reglement === TypeReglement.MOUVEMENT && (
        <SelectNatureReglement
          onNatureReglementChange={handleOnNatureChange}
          selectedNatureReglement={filters.nature}
          includeAllOption
        />
      )}

      {filters.type_reglement === TypeReglement.MOUVEMENT && (
        <FormControl fullWidth size="small">
          <InputLabel id="sens-mouvement">Sens</InputLabel>
          <Select
            labelId="sens-mouvement"
            value={filters.sens ?? ''}
            onChange={handleSensChange}
            label="Sens"
          >
            <MenuItem value=""><em>Tous</em></MenuItem>
            <MenuItem value="C">Crédit</MenuItem>
            <MenuItem value="D">Débit</MenuItem>
          </Select>
        </FormControl>
      )}

      <TextField
        label="Date début"
        type="date"
        size="small"
        fullWidth
        value={filters.date_start || ''}
        onChange={handleDateChange('date_start')}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <TextField
        label="Date fin"
        type="date"
        size="small"
        fullWidth
        value={filters.date_end || ''}
        onChange={handleDateChange('date_end')}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <TextField
        label="Montant"
        type="number"
        size="small"
        fullWidth
        value={montantLocal}
        onChange={handleMontantChange}
        slotProps={{ input: { inputProps: { min: 0 } } }}
      />

      <AppFilterResetButton onClick={handleReset} />
    </AppFilterGrid>
  );
};

export default ReglementFilters;
